import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  VMEJobScreenAllocateJob,
  VMEJobScreenJob,
} from '../../../interfaces/screen-specific/job-screen';
import { Location } from '@angular/common';
import {
  EventService,
  JobsDeploymentService,
  JobsService,
  TelephonyService,
  VmeInboxJobsService,
} from '../../../services';
import { ActivatedRoute, Router } from '@angular/router';
import { JobScreenCommonService } from '../common/job-screen-common.service';
import { OrganisationSelectorEventTypeEnum } from '../../../interfaces/telephony/organisation-selector-event-type.enum';
import { IOrganisationSelectorEvent } from '../../../interfaces/telephony/organisation-selector-event';
import { Subscription } from 'rxjs';
import { OrganisationTypeEnum } from '../../../enums/organisation-types';
import { Message } from '../../../classes/Message';
import { uniqBy } from 'lodash';
import { MessageService } from 'primeng';
import { ITelephonyEvent, TelephonyEventEnum } from '../../../interfaces/telephony/telephony-event';
import { RefreshJobEventTypeEnum } from '../../../enums/refresh-job-event-type.enum';
import { JobStatus } from '../../job-control/job-screen.component';
import { JobAnalyticsService } from '../../../services/product-analytics/job-analytics.service';
import { TabType } from '../common/tab-scroller/tab-scroller.component';
import { ILogCallJobInfo } from '../../../interfaces/telephony/log-call-job-info';

@Component({
  selector: 'app-vme-job-screen',
  templateUrl: './vme-job-screen.component.html',
  styleUrls: ['./vme-job-screen.component.scss', '../common/job-screen-common.scss'],
})
export class VMEJobScreenComponent implements OnInit, OnDestroy {
  @Input('organisationId') organisationId!: number;
  @Input('organisationTypeId') organisationTypeId!: number;

  jobId: number;
  job: VMEJobScreenJob = null;

  allocateSupplier = null;
  tabs: TabType[] = [];
  selectedTask: any;
  showAllocateJobDialog: boolean;
  allocationPrice: number;
  jobAllocationInfo: VMEJobScreenAllocateJob;
  customerLinkData: any[] = [];
  selectedOrderId: number | null = null;

  subscriptions: Subscription[] = [];

  public activeViewId: string = 'scroll';

  public get canShowPriceDetails(): boolean {
    return this.job && this.job.statusId != JobStatus.Invoiced;
  }

  constructor(
    private location: Location,
    public commonService: JobScreenCommonService,
    private jobService: JobsService,
    private messageService: MessageService,
    private vmeJobService: VmeInboxJobsService,
    private route: ActivatedRoute,
    private router: Router,
    private jobDeploymentService: JobsDeploymentService,
    private eventService: EventService,
    public telephonyService: TelephonyService,
    private jobAnalyticsService: JobAnalyticsService,
  ) {
    this.initSubscriptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x?.unsubscribe());
  }

  async ngOnInit() {
    this.jobId = parseInt(this.route.snapshot.paramMap.get('id'));

    // this.jobService
    //   .get(this.jobId)
    //   .then((res) =>
    //     this.jobAnalyticsService.sendJobDetailsViewEvent(res, this.organisationTypeId),
    //   );

    this.tabs = [
      { label: 'Job Information', id: 'jobDetails', type: 'scroll' },
      { label: 'Order Details', id: 'orderDetails', type: 'scroll' },
      { label: 'Task Details', id: 'taskDetails', type: 'scroll' },
    ];

    this.getJob(this.jobId).then(() => {});
  }

  showPriceDetails() {
    const param: any = {
      jobId: this.jobId,
    };

    if (this.selectedOrderId) {
      param.orderId = this.selectedOrderId;
    }

    this.router.navigate(['app/pricedetails'], { queryParams: param });
  }

  initSubscriptions() {
    this.subscriptions.push(
      this.eventService.JobScreenEvent.subscribe((event: any) => {
        this.handleEvents(event);
      }),
    );

    this.subscriptions.push(
      this.eventService.OrganisationSelectorEvent.subscribe((event: IOrganisationSelectorEvent) => {
        if (event.eventType == OrganisationSelectorEventTypeEnum.DeploySupplierSelected) {
          this.deploySupplierSelected(event);
        }
      }),
    );

    this.subscriptions.push(
      this.eventService.InternalTelephonyEvent.subscribe((event: ITelephonyEvent) => {
        if (event.eventType == TelephonyEventEnum.CallLogged) {
          // refresh history section
          this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
        }
      }),
    );
  }

  private deploySupplierSelected(event: any) {
    this.jobService
      .getForComponent(this.jobId, 'vme-job-screen-allocate')
      .then((job: VMEJobScreenAllocateJob) => {
        this.allocateSupplier = event.args;

        this.allocationPrice = !this.job.buyItNowPriceOriginal
          ? this.job.buyItNowPrice
          : this.job.buyItNowPriceOriginal;

        this.jobAllocationInfo = {
          price: this.allocationPrice,
          id: this.job.id,
          tasks: job.tasks,
          supplierTags: job.supplierTags,
        };

        this.showAllocateJobDialog = true;
      });
  }

  canDeploy() {
    return (
      this.job != null &&
      this.organisationTypeId == OrganisationTypeEnum.Engineius &&
      !this.job.supplierId
    );
  }

  markPriority() {
    this.vmeJobService.markPriorityDeployment([this.jobId]).then(() => {
      this.job.priorityDeployment = !this.job.priorityDeployment;
    });
  }

  public onAllocateJobSaved() {
    void this.getJob(this.jobId);

    this.eventService.RefreshIncidentsEvent.emit();

    this.eventService.JobScreenEvent.emit({
      event: 'refresh-notes',
      data: { jobId: this.jobId },
    });

    this.eventService.JobScreenEvent.emit({
      event: 'refresh-supplier',
      data: { jobId: this.jobId },
    });
  }

  changePriority() {
    this.vmeJobService.markPriorityDeployment([this.jobId]).then(() => {});
  }

  async getJob(jobId: number) {
    const job = await this.jobService.getForComponent<VMEJobScreenJob>(jobId, 'vme-job-screen');

    if (job.redirectId) {
      window.location.href = `/app/jobscreen/${job.redirectId}`;
    }

    this.job = job;

    this.customerLinkData = uniqBy(job.orders, 'customer.name').map((x) => {
      return {
        label: x.customer.name,
        infoURL: x.customer.companyInfoURL,
        icon: 'pi pi-external-link',
        command: () => {
          this.linkToCustomerInfo(x.customer.companyInfoURL);
        },
      };
    });

    this.syncItineraryTab();
  }

  syncItineraryTab(): void {
    const hasItineraryTab = this.tabs.some((tab) => tab.id === 'routeItinerary');

    if (this.job.routeRecommendationId && !hasItineraryTab) {
      this.tabs = [...this.tabs, { label: 'Itinerary', id: 'routeItinerary', type: 'component' }];
    } else if (!this.job.routeRecommendationId && hasItineraryTab) {
      this.tabs = this.tabs.filter((tab) => tab.id !== 'routeItinerary');
    }
  }

  private handleEvents(event: any) {
    if (event.event == 'job-details-changed') {
      this.getJob(this.jobId).then(() => {});
    }

    if (event.event == 'order-changed') {
      this.selectedOrderId = event.data.orderId;
    }
  }

  showHistory() {}

  goBack() {
    this.location.back();
  }

  jobDeployment() {
    this.router.navigate(['app/job-deployment', this.jobId]);
  }

  deployJobToSupplier() {
    this.eventService.OrganisationSelectorEvent.emit({
      eventType: OrganisationSelectorEventTypeEnum.OpenSelector,
      args: true,
      forDeployment: true,
      orgType: OrganisationTypeEnum.Supplier,
    });
  }

  onLogCall() {
    this.jobService.getForComponent(this.jobId, 'log-call').then((job: ILogCallJobInfo) => {
      this.eventService.InternalTelephonyEvent.emit({
        eventType: TelephonyEventEnum.LogCall,
        telephonyJobInfo: job,
      });
    });
  }

  linkToCustomerInfo(url: string) {
    if (!url || url.trim() == '') {
      if (this.customerLinkData && this.customerLinkData.length > 0)
        url = <string>this.customerLinkData[0].infoURL;

      if (!url) {
        this.messageService.clear();
        this.messageService.add(
          Message.Info('The selected company does not have an information URL specified'),
        );
        return;
      }
    }

    this.openInNewTab(url);
  }

  openInNewTab(href) {
    Object.assign(document.createElement('a'), {
      target: '_blank',
      href,
    }).click();
  }

  onTabSelected(tab: TabType): void {
    if (tab.type === 'component') {
      this.activeViewId = tab.id;
    } else {
      this.activeViewId = 'scroll';
    }
  }

  loadRoutesInbox(): void {
    const notAssigned = this.canDeploy();

    const queryParams: any = {
      ['jobNumberOrVehicleReg']: this.job.jobNumber,
    };

    if (!notAssigned) {
      queryParams.extender = 2; //If it's already grabbed then we know we're filtering on Extensions - so default search to this :)
    }

    this.router.navigate(['app/routes-inbox'], {
      queryParams: queryParams,
    });
  }

  onAdditionalCostUploadError(event: string) {
    this.messageService.add(Message.Error(event, 'Additional Cost File Upload'));
  }

  public canQueueAutoDirect(): boolean {
    return this.job != null && !this.job.supplierId && this.job.statusId === JobStatus.Created;
  }
}
